import { type UnicodeEmojiPacks } from "@revolt/markdown/emoji/packs";
import { batch } from "solid-js";

import type { State } from "..";

import { AbstractStore } from ".";

/**
 * Possible notification permission states
 */
export type NotificationPermissionState =
  | "default"
  | "denied"
  | "allowed"
  | "unsupported";

/**
 * Possible notification permission states
 */
const NotificationPermissionStates: NotificationPermissionState[] = [
  "default",
  "denied",
  "allowed",
  "unsupported",
];

interface SettingsDefinition {
  /**
   * Whether to enable desktop notifications
   */
  "notifications:desktop": NotificationPermissionState;

  /**
   * Whether to enable push notifications
   */
  "notifications:push": NotificationPermissionState;

  /**
   * Selected unicode emoji
   */
  "appearance:unicode_emoji": UnicodeEmojiPacks;

  // TODO: this should be part of theme
  // "appearance:ligatures": boolean;

  /**
   * Enable season effects
   * TODO: implement
   */
  // "appearance:seasonal": boolean;

  // TODO: this should be part of theme
  // "appearance:transparency": boolean;

  /**
   * Show message send button
   */

  /**
   * Whether to render messages in compact mode
   */
  "appearance:compact_mode": boolean;

  /**
   * Indicate new users to Stoat
   * TODO: implement
   */
  // "appearance:show_account_age": boolean;

  /**
   * Whether to include 'copy ID' in context menus
   */
  "advanced:copy_id": boolean;
}

/**
 * Map actual type to JavaScript type OR function to clean the value.
 */
type ValueType<T extends keyof SettingsDefinition> =
  SettingsDefinition[T] extends boolean
    ? "boolean"
    : SettingsDefinition[T] extends number
      ? "number"
      : SettingsDefinition[T] extends string
        ? "string"
        : (
            v: Partial<SettingsDefinition[T]>,
          ) => SettingsDefinition[T] | undefined;

/**
 * Expected types of settings keys, enforce some sort of validation is present for all keys.
 * If we cannot validate the value as a primitive, clean it up using a function.
 */
const EXPECTED_TYPES: { [K in keyof SettingsDefinition]: ValueType<K> } = {
  "notifications:desktop": "string",
  "notifications:push": "string",
  "appearance:unicode_emoji": "string",
  "appearance:compact_mode": "boolean",
  "advanced:copy_id": "boolean",
};

/**
 * In reality, this is a partial so we map it accordingly here.
 */
export type TypeSettings = Partial<SettingsDefinition>;

/**
 * The defaults, in one place.
 *
 * These are resolved live rather than written to disk. `default()` seeds the
 * in-memory store from them, and `getValue` falls back to them for any key the
 * user has not set, so changing a value here reaches everybody who never made
 * a choice - which is the point of a default.
 */
const DEFAULT_VALUES: TypeSettings = {
  "notifications:desktop": "default",
  "notifications:push": "default",
  // twemoji, not fluent-3d. The 3D pack renders each glyph as an illustration:
  // 52 KB per emoji against twemoji's 450, and every one is its own request to
  // the CDN. A picker row is ten wide, so opening it used to pull megabytes
  // before the first screen settled.
  "appearance:unicode_emoji": "twemoji",
  "appearance:compact_mode": false,
  "advanced:copy_id": false,
};

/**
 * Settings store
 */
export class Settings extends AbstractStore<"settings", TypeSettings> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "settings");
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeSettings {
    return { ...DEFAULT_VALUES };
  }

  /**
   * Keep the defaults out of the file.
   *
   * `clean()` alone could not do this. Its result goes through the store
   * setter, which merges rather than replaces, so the defaults seeded into
   * memory at construction were merged straight back in and the disk write
   * serialises memory. Every profile ended up with the day's defaults written
   * into it, which is how a pack nobody chose outlived three attempts to change
   * it. Stripping here is the last point before the file.
   *
   * A value equal to the current default is dropped even when it was chosen on
   * purpose: the two are the same value and nothing on disk can tell them
   * apart. The cost is that such a choice follows the default if it later
   * moves; the alternative is that no default can ever move again.
   */
  toDisk(data: TypeSettings): TypeSettings {
    return this.dropDefaults(data);
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeSettings>): TypeSettings {
    // Starts empty on purpose. This runs on hydrate and its result is written
    // back to disk, so seeding it from the defaults persisted them into every
    // profile the first time the app stored anything at all - after which they
    // were indistinguishable from a deliberate choice and no later change to a
    // default could ever reach that user. Only what was actually set is kept.
    const settings: TypeSettings = {};

    for (const key of Object.keys(input) as (keyof TypeSettings)[]) {
      const expectedType = EXPECTED_TYPES[key];

      if (typeof expectedType === "function") {
        const cleanedValue = (expectedType as (value: unknown) => unknown)(
          input[key],
        );
        if (cleanedValue) {
          settings[key] = cleanedValue as never;
        }
      } else if (key === "appearance:unicode_emoji") {
        // Dropped, never kept. The pack is not a choice any more, and a value
        // already sitting in a profile would otherwise go on answering for it:
        // the picker is gone but the old writes are not, and the store cannot
        // tell one that was chosen from one that was merely the default of the
        // day. Refusing to carry it forward is what makes the setting single
        // valued for everybody rather than only for new profiles.
      } else if (key === "notifications:desktop") {
        if (NotificationPermissionStates.includes(input[key] as never)) {
          settings[key] = input[key];
        }
      } else if (key === "notifications:push") {
        if (NotificationPermissionStates.includes(input[key] as never)) {
          settings[key] = input[key];
        }
      } else if (typeof input[key] === expectedType) {
        settings[key] = input[key] as never;
      }
    }

    return settings;
  }

  /**
   * Set a settings key
   * @param key Colon-divided key
   * @param value Value
   */
  setValue<T extends keyof TypeSettings>(key: T, value: TypeSettings[T]) {
    this.set(key, value);
  }

  /**
   * Get a settings key
   * @param key Colon-divided key
   * @returns Value at key or default value
   */
  getValue<T extends keyof TypeSettings>(key: T) {
    return this.get()[key] ?? DEFAULT_VALUES[key];
  }

  /**
   * Get the permission state for desktop notifications
   */
  get desktopNotificationsState(): NotificationPermissionState {
    return this.getValue("notifications:desktop") ?? "default";
  }

  /**
   * Get the permission state for push notifications
   */
  get pushNotificationsState(): NotificationPermissionState {
    return this.getValue("notifications:push") ?? "default";
  }

  /**
   * Set the permission state for desktop notifications. If deskop notifications are ever set to `unsupported` this function will noop.
   */
  set desktopNotificationsState(newState: NotificationPermissionState) {
    if (this.desktopNotificationsState !== "unsupported") {
      this.setValue("notifications:desktop", newState);
    }
  }

  /**
   * Set the permission state for push notifications. If newState is `unsupported` this function will noop.
   */
  set pushNotificationsState(newState: NotificationPermissionState) {
    if (newState !== "unsupported") {
      this.setValue("notifications:push", newState);
    }
  }

  /**
   * Reset the notifications state for both desktop and push notifications.
   * @param newState The state to set both notification states to. Defaults to "default"
   */
  resetNotificationsState(newState?: "default" | "denied") {
    batch(() => {
      // Use setValue here instead of the setter as we want to bypass the unsupported block.
      this.setValue("notifications:desktop", newState ?? "default");
      this.pushNotificationsState = newState ?? "default";
    });
  }
}
